import { Injectable, inject, signal, effect } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { ApiHttpService } from './api.service'
import { ToastController } from '@ionic/angular/standalone'
import { Preferences } from '@capacitor/preferences'
import { AuthService } from './auth'
import { MatrixService } from './matrix.service'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

@Injectable({providedIn: 'root'})
export class FcmService {
    private messaging = inject(Messaging);
    private apiService = inject(ApiHttpService);
    private toastController = inject(ToastController);
    private authService = inject(AuthService);
    private matrixService = inject(MatrixService);
    private router = inject(Router);
    private translate = inject(TranslateService);


    public showPermissionModalTrigger = signal(false);
    public notificationsEnabled = signal<boolean>(false);
    private readonly STORAGE_KEY = 'notifications_enabled';
    private isLoaded = false;

    private allRooms = this.matrixService.rooms;

    private isListeningForMessages = false;


    constructor() {
        this.init();
        effect(async () => {
            const enabled = this.notificationsEnabled();

            if (!this.isLoaded) return;

            await Preferences.set({
                key: this.STORAGE_KEY, value: String(enabled)
            });

            if (enabled) {
                await this.triggerPermissionCheck();
            }
        });
    }

    private async init() {
        await this.loadInitialState();
    }

    private async loadInitialState() {
        const {value} = await Preferences.get({key: this.STORAGE_KEY});
        if (value === 'true') {
            this.notificationsEnabled.set(true);
        }
        this.isLoaded = true;
    }

    async toggleNotifications(
        event: CustomEvent<{ checked: boolean }>
    ): Promise<void> {
        const enabled = event.detail.checked;

        if (!enabled) {
            this.notificationsEnabled.set(false);
            this.showPermissionModalTrigger.set(false);
            this.updatePushStatus();
            return;
        }

        try {
            let permission = Notification.permission;

            if (permission === 'default') {
                this.notificationsEnabled.set(false);
                this.showPermissionModalTrigger.set(true);
                return;
            }

            if (permission === 'denied') {
                this.notificationsEnabled.set(false);
                this.showPermissionModalTrigger.set(false);
                this.updatePushStatus();
                return;
            }

            const token = await getToken(this.messaging, {
                vapidKey: 'BHx_f13O7Ebwj-YQfojKehpnLUAJ53n_TpNDp7rY7V01NZizw-XuQWR3S_OBMSBUfSnpchtLWG-92hwcTz2QW6o'
            });

            if (!token) {
                throw new Error('FCM token was not created');
            }

            this.notificationsEnabled.set(true);
            this.showPermissionModalTrigger.set(false);
            this.updatePushStatus();
            this.saveTokenToBackend(token);
            this.listenForMessages();

        } catch (error) {
            console.error('Notification activation error:', error);
            this.showPermissionModalTrigger.set(false);
            this.notificationsEnabled.set(false);
            this.updatePushStatus();
        }
    }

    async checkPermissionStatus() {
        if (!('Notification' in window)) {
            return 'denied';
        }
        return Notification.permission;
    }

    async triggerPermissionCheck() {
        const permission = await this.checkPermissionStatus();
        if (permission === 'denied') {
            this.notificationsEnabled.set(false);
            this.showPermissionModalTrigger.set(false);
            return;
        }
        if (permission === 'default') {
            this.notificationsEnabled.set(false);
            this.showPermissionModalTrigger.set(true);
            return;
        }
        this.showPermissionModalTrigger.set(false);

        if (this.notificationsEnabled()) {
            await this.registerPushToken();
            this.listenForMessages();
        }
    }

    async requestPermission() {
        this.showPermissionModalTrigger.set(false);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                this.notificationsEnabled.set(false);
                this.updatePushStatus();

                console.warn('Notification permission:', permission);
                return;
            }

            this.notificationsEnabled.set(true);
            this.updatePushStatus();

            await this.registerPushToken();
            this.listenForMessages();

        } catch (error) {
            this.notificationsEnabled.set(false);
            this.updatePushStatus();
            console.error('Error getting token:', error);
        }
    }

    private async registerPushToken(): Promise<void> {
        const token = await getToken(this.messaging, {
            vapidKey: 'BHx_f13O7Ebwj-YQfojKehpnLUAJ53n_TpNDp7rY7V01NZizw-XuQWR3S_OBMSBUfSnpchtLWG-92hwcTz2QW6o'
        });

        if (token) {
            console.log('FCM Token:', token);
            this.saveTokenToBackend(token);
        }
    }

    listenForMessages() {
         if (this.isListeningForMessages) {
            return;
        }
        this.isListeningForMessages = true;
        onMessage(this.messaging, (payload: any) => {
            console.log('Received a focused message:', payload);

            const payloadRoomId = payload.data?.room_id;

            if (!payloadRoomId) {
                return;
            }
            let currentRoomId = this.getCurrentRoomId();

            if (currentRoomId === payloadRoomId) {
                return;
            }

            const room = this.allRooms().find(r => r.roomId === payloadRoomId);

            const msg = room?.name;
            const title = msg ? this.translate.instant('chat_detail.new_message') + ': ' + msg : this.translate.instant('chat_detail.new_message');
            this.presentToast(`${title}`);
        });
    }

    private getCurrentRoomId(): string | null {
        const match = this.router.url.match(/^\/chat\/(.+)$/);

        if (!match) {
            return null;
        }

        return decodeURIComponent(decodeURIComponent(match[1]));
    }

    private saveTokenToBackend(token: string) {
        let matrixAccessToken = this.authService.userData().matrixAccessToken || '';
        this.apiService.post('forum-talk-push-tokens', {
            push_token: token,
            matrix_access_token: matrixAccessToken,
        }).subscribe({
            next: () =>
                console.log('Token saved on server'),
            error: (err) =>
                console.error('Failed to save token on server', err)
        });
    }

    updatePushStatus () {
        this.authService.updatePushStatus(this.notificationsEnabled()).subscribe({
            next: () =>
                console.log('Push status updated on server'),
            error: (err: any) =>
                console.error('Failed to update push status on server', err)
        });
    }

    async presentToast(message: string) {
        const toast = await this.toastController.create({
            message: message, duration: 3000, position: 'top',
        });

        await toast.present();
    }
}
